import { SvgPathBuilder } from '../svg/SvgPathBuilder';
import { solveCubic } from '../utils/solveCubic';
import { POINT_SVG_MATCH_STR, type Point, point } from './point';
import { type Vector, vector } from './vector';

/**
 * The implementation supports two SVG commands: `M` and `Q`.
 */
const QUADRATIC_BEZIER_CURVE_SVG_MATCH_STR =
  `M\\s*${POINT_SVG_MATCH_STR}\\s*` +
  `Q\\s*${POINT_SVG_MATCH_STR}\\s*,?\\s*${POINT_SVG_MATCH_STR}`;
const QUADRATIC_BEZIER_CURVE_SVG_MATCH_RE = new RegExp(
  `^${QUADRATIC_BEZIER_CURVE_SVG_MATCH_STR}$`,
);
type QuadraticBezierCurveSvgRegExpMatch = [
  all: string,
  startX: string,
  startY: string,
  cx: string,
  cy: string,
  endX: string,
  endY: string,
];

/**
 * Part of quadratic bezier curve without start, useful for defining continuous lines
 *
 * @see QuadraticBezierCurve
 */
export interface QuadraticBezierLinePart {
  readonly c: Point;
  readonly e: Point;
}

/**
 * Implements quadratic Bézier curve
 *
 * Quadratic Bézier curves are smooth curve definitions using three points:
 * - `s` - starting point (current point)
 * - `c` - control point (curve starts in direction s->c and ends in c->e)
 * - `e` - end point.
 *
 * Attribute names are intentionally short since this object will be
 * often passed via API.
 * Quadratic Bézier curves are weaker than cubic, but way easier to compute
 * (e.g. distance from quadratic curve can be computed directly using calculus
 * by solving 3rd degree equation, which is not possible to do with cubic one
 * since it leads to 5th degree equation.
 */
export interface QuadraticBezierCurve extends QuadraticBezierLinePart {
  readonly s: Point;
}

export function create(s: Point, c: Point, e: Point): QuadraticBezierCurve {
  return { s, c, e };
}

export function createLinePart(c: Point, e: Point): QuadraticBezierLinePart {
  return { c, e };
}

export function fromLinePart(
  p: QuadraticBezierLinePart,
  s: Point,
): QuadraticBezierCurve {
  return {
    ...p,
    s,
  };
}

export function equals(
  a: QuadraticBezierCurve,
  b: QuadraticBezierCurve,
): boolean {
  return (
    point.equals(a.s, b.s) && point.equals(a.c, b.c) && point.equals(a.e, b.e)
  );
}

/**
 * Create an instance from SVG's `d` attribute
 */
export function fromSvgString(d: string): QuadraticBezierCurve {
  const match = d.trim().match(QUADRATIC_BEZIER_CURVE_SVG_MATCH_RE);
  if (!match) {
    throw new Error(`Unexpected SVG path: ${JSON.stringify(d)}`);
  }

  const [, startX, startY, cx, cy, endX, endY] =
    match as QuadraticBezierCurveSvgRegExpMatch;

  return create(
    point.create(+startX, +startY),
    point.create(+cx, +cy),
    point.create(+endX, +endY),
  );
}

export function getD(curve: QuadraticBezierCurve, compact = false): string {
  return new SvgPathBuilder(compact)
    .moveTo(curve.s)
    .quadraticBezierTo(curve.c)(curve.e)
    .build();
}

/**
 * @param curve
 * @param t {number} - a number from 0 to 1 that represents location on the curve
 */
export function getPoint(curve: QuadraticBezierCurve, t: number): Point {
  if (t > 1 || t < 0) {
    throw new RangeError();
  }
  // blending functions - they control the "effect" of the control point on the curve
  const tc = 1 - t;
  const a = tc * tc;
  const b = 2 * t * tc;
  const c = t * t;
  return point.create(
    a * curve.s.x + b * curve.c.x + c * curve.e.x,
    a * curve.s.y + b * curve.c.y + c * curve.e.y,
  );
}

export function getSegmentLength(curve: QuadraticBezierCurve): number {
  return point.getDistance(curve.s, curve.e);
}

/**
 * Squared distance is computationally cheaper since it does not involve the final square root.
 * If you need an ordinary euclidean distance, just use Math.sqrt on the result.
 *
 * Inspired by http://www.pouet.net/topic.php?which=9119&page=1#c429055
 */
export function getSquaredDistance(
  curve: QuadraticBezierCurve,
  p: Point,
): number {
  // distance from the curve = distance from point X on the curve where the curve's derivation
  // is perpendicular to pX (local minima)
  const m = vector.sub(curve.s, p);
  const a = vector.sub(curve.c, curve.s);
  const b = vector.sub(vector.sub(curve.e, curve.c), a);

  const solutions = solveCubic(
    vector.dot(b, b),
    3 * vector.dot(a, b),
    2 * vector.dot(a, a) + vector.dot(m, b),
    vector.dot(m, a),
  );

  const closest = solutions
    // filter solutions that lie out of the bezier curve bounds (t in [0, 1])
    .filter((t) => t >= 0 && t <= 1)
    // find the closest one (or keep infinity if none is left)
    .reduce(
      (min, t) =>
        Math.min(min, point.getSquaredDistance(p, getPoint(curve, t))),
      Number.POSITIVE_INFINITY,
    );

  // distance = minimum of distance from closest point on the curve and starting and ending point
  return Math.min(
    closest,
    point.getSquaredDistance(p, curve.s),
    point.getSquaredDistance(p, curve.e),
  );
}

export function toVector(curve: QuadraticBezierCurve): Vector {
  return vector.fromAtoB(curve.s, curve.e);
}

export const quadraticBezierCurve = {
  create,
  createLinePart,
  equals,
  fromLinePart,
  fromSvgString,
  getD,
  getPoint,
  getSegmentLength,
  getSquaredDistance,
  toVector,
};
